import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  IonInfiniteScroll,
  IonInfiniteScrollContent,
  IonRefresher,
  IonRefresherContent,
  IonButton,
  IonToast,
  RefresherEventDetail,
} from '@ionic/react';
import { documentTextOutline } from 'ionicons/icons';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../../lib/api';
import { Bill } from '../../types/bill';
import ProMobileBillCard from '../../components/pro/ProMobileBillCard';
import ProFilterChips from '../../components/pro/ProFilterChips';
import ProTrackingDestinationLabel from '../../components/pro/ProTrackingDestinationLabel';
import ProTrackingWorkspacePicker from '../../components/pro/bottom-sheet/ProTrackingWorkspacePicker';
import { ProEmptyState, ProLoadingList, ProListToolbar } from '../../components/pro/primitives';
import {
  PRO_BILL_DEFAULT_FILTERS,
  PRO_BILL_FILTER_DEFS,
  PRO_BILL_FILTER_KEYS,
  resolveProFilterDefs,
  withSessionFilterOptions,
} from '../../components/pro/proFilterDefs';
import { useDebounce } from '../../hooks/useDebounce';
import { useProListUrlState } from '../../hooks/useProListUrlState';
import { useSessionOptions } from '../../hooks/useSessions';
import { useProTrackingWorkspace } from './useProTrackingWorkspace';

const PAGE_SIZE = 20;

const ProBillsPage: React.FC = () => {
  const { t } = useTranslation('pro');
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const sponsorFilter = searchParams.get('sponsor') || undefined;

  const { options: sessionOptions } = useSessionOptions(false);

  const BILL_FILTERS = React.useMemo(
    () =>
      resolveProFilterDefs(
        withSessionFilterOptions(PRO_BILL_FILTER_DEFS, sessionOptions),
        t,
      ),
    [sessionOptions, t],
  );

  const {
    search,
    setSearch,
    activeFilters,
    handleFilterChange,
    handleClearFilters,
  } = useProListUrlState(PRO_BILL_FILTER_KEYS, PRO_BILL_DEFAULT_FILTERS);
  const [showError, setShowError] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);

  const tracking = useProTrackingWorkspace();
  const { status, eligible, select } = tracking;
  const selectedWorkspace =
    tracking.status === 'selected' ? tracking.workspace : null;
  const workspaceId = selectedWorkspace?.id ?? null;

  const debouncedSearch = useDebounce(search, 300);
  const chamber = activeFilters.chamber;
  const billStatus = activeFilters.bill_status;
  const session = activeFilters.session;

  const {
    data,
    isLoading,
    fetchNextPage,
    hasNextPage,
    refetch,
  } = useInfiniteQuery(
    ['pro-bills', { search: debouncedSearch, chamber, bill_status: billStatus, session, sponsor: sponsorFilter }],
    async ({ pageParam = 0 }) => {
      const response = await api.get('/bills', {
        params: {
          limit: PAGE_SIZE,
          offset: pageParam,
          search: debouncedSearch || undefined,
          chamber: chamber || undefined,
          bill_status: billStatus || undefined,
          session: session || undefined,
          sponsor: sponsorFilter,
        },
      });
      const result = response.data?.data || response.data;
      const bills: Bill[] = result?.bills || (Array.isArray(result) ? result : []);
      const total: number = result?.total || bills.length;
      return { bills, total, offset: pageParam };
    },
    {
      staleTime: 30000,
      keepPreviousData: true,
      getNextPageParam: (lastPage, allPages) => {
        const offset = allPages.length * PAGE_SIZE;
        return offset < lastPage.total ? offset : undefined;
      },
      onError: () => setShowError(true),
    },
  );

  // Workspace-scoped tracked set (same endpoint as ProWorkspaceDetailPage /
  // ProBillDetailPage). Do not use user-tracked-bills for indicators — with no
  // selection, show an empty tracked set.
  const { data: trackedData } = useQuery(
    ['pro-tracked-bills', workspaceId],
    async () => {
      if (!workspaceId) return [];
      const { data } = await api.get(`/workspace-bill-tracking/workspace/${workspaceId}`);
      const result = data?.data ?? data;
      const bills = Array.isArray(result)
        ? result
        : result?.bills || result?.items || [];
      return bills;
    },
    { staleTime: 60000, enabled: !!workspaceId },
  );

  const trackedBillIds: Set<string> = React.useMemo(() => {
    if (!workspaceId) return new Set<string>();
    const raw = Array.isArray(trackedData) ? trackedData : [];
    const ids = new Set<string>();
    for (const item of raw) {
      if (item == null) continue;
      if (typeof item === 'string' || typeof item === 'number') {
        ids.add(String(item));
        continue;
      }
      if (typeof item === 'object') {
        const row = item as {
          id?: unknown;
          bill_id?: unknown;
          billId?: unknown;
          billNumber?: unknown;
          bill_number?: unknown;
        };
        if (row.bill_id != null) ids.add(String(row.bill_id));
        if (row.billId != null) ids.add(String(row.billId));
        if (row.id != null) ids.add(String(row.id));
        if (row.billNumber != null) ids.add(String(row.billNumber));
        if (row.bill_number != null) ids.add(String(row.bill_number));
      }
    }
    return ids;
  }, [trackedData, workspaceId]);

  const allBills = data?.pages.flatMap((p) => p.bills) ?? [];
  const hasActiveFilters =
    chamber !== '' || billStatus !== '' || session !== '' || !!sponsorFilter;
  const isEmpty = !isLoading && allBills.length === 0;

  const handleClearSponsorFilter = () => {
    const next = new URLSearchParams(searchParams);
    next.delete('sponsor');
    setSearchParams(next, { replace: true });
  };

  const handleRefresh = async (e: CustomEvent<RefresherEventDetail>) => {
    await refetch();
    e.detail.complete();
  };

  const handleBillClick = (bill: Bill) => {
    navigate(`/pro/bills/${bill.billNumber}`);
  };

  return (
    <>
      <IonRefresher slot="fixed" onIonRefresh={handleRefresh}>
        <IonRefresherContent />
      </IonRefresher>

      <div className="max-w-3xl mx-auto">
        {sponsorFilter && (
          <div className="px-4 pt-4 pb-2">
            <div className="flex items-center gap-2">
              <span className="text-xs text-text-secondary dark:text-text-dark-secondary">
                {t('bills.filteredBySponsor')}
              </span>
              <IonButton
                fill="clear"
                size="small"
                className="text-xs"
                onClick={handleClearSponsorFilter}
              >
                {t('bills.clearSponsorFilter')}
              </IonButton>
            </div>
          </div>
        )}

        <ProListToolbar
          value={search}
          placeholder={t('bills.searchPlaceholder')}
          onSearchChange={setSearch}
        >
          <ProFilterChips
            filters={BILL_FILTERS}
            activeFilters={activeFilters}
            onFilterChange={handleFilterChange}
          />
        </ProListToolbar>

        <div className="px-4 pt-1">
          <ProTrackingDestinationLabel
            status={status}
            workspace={selectedWorkspace}
            eligibleCount={eligible.length}
            onPress={() => setPickerOpen(true)}
            disabled={status === 'none' || status === 'context-ineligible'}
          />
        </div>

        <div className="px-4 pb-4 space-y-3 mt-2">
          {isLoading ? (
            <ProLoadingList variant="card" count={4} className="space-y-3" />
          ) : isEmpty ? (
            <ProEmptyState
              icon={documentTextOutline}
              message={t('bills.emptyState')}
              actionLabel={(hasActiveFilters || debouncedSearch) ? t('common.clearFilters') : undefined}
              onAction={(hasActiveFilters || debouncedSearch) ? () => handleClearFilters(['sponsor']) : undefined}
            />
          ) : (
            allBills.map((bill) => (
              <ProMobileBillCard
                key={bill.id || bill.billNumber}
                bill={bill}
                workspaceName={
                  trackedBillIds.has(String(bill.id)) ||
                  trackedBillIds.has(String(bill.billNumber || ''))
                    ? (selectedWorkspace?.name || t('common.tracked'))
                    : undefined
                }
                onClick={handleBillClick}
              />
            ))
          )}
        </div>

        <IonInfiniteScroll
          disabled={!hasNextPage}
          onIonInfinite={(e) => {
            fetchNextPage().then(() => (e.target as HTMLIonInfiniteScrollElement).complete());
          }}
        >
          <IonInfiniteScrollContent loadingText={t('bills.loadingMore')} />
        </IonInfiniteScroll>
      </div>

      <ProTrackingWorkspacePicker
        isOpen={pickerOpen}
        eligible={eligible}
        selectedId={workspaceId}
        onSelect={(id) => {
          select(id);
          setPickerOpen(false);
        }}
        onDismiss={() => setPickerOpen(false)}
      />

      <IonToast
        isOpen={showError}
        onDidDismiss={() => setShowError(false)}
        message={t('bills.errorMessage')}
        duration={4000}
        buttons={[
          {
            text: t('common.retry'),
            handler: () => {
              setShowError(false);
              refetch();
            },
          },
        ]}
        color="danger"
      />
    </>
  );
};

export default ProBillsPage;
